import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Paper,
  Card,
  CardContent,
  Typography,
  Container,
  Box,
  Breadcrumbs,
  Link,
  Fade,
} from "@mui/material";
import Icon from "../../components/Icon";
import { CustomChartsTitle } from "../../components/Custom/CustomTypography";
import CustomAppBar from "../../components/AppBar";

const CropTraceActivities = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const farmId = searchParams.get("farmId");
  const cropId = searchParams.get("cropId");

  const activities = [
    {
      label: t("Satellite Data"),
      description: t("View satellite imagery and analysis for your farm"),
      type: "satelliteDataQL",
      route: `/farmer/satellite-data-farm/${farmId}`,
    },
    {
      label: t("Fertigation"),
      description: t("Record pesticides and fertilizers used"),
      type: "inputSupplyQL",
      route: `/farmer/create-persicide-fertilizers?cropId=${cropId}`,
    },
    {
      label: t("Lab Test"),
      description: t("Record and view lab test results"),
      type: "labTestQL",
      route: `/farmer/create-lab-test/?cropId=${cropId}`,
    },
    {
      label: t("My Harvest"),
      description: t("Record harvest data and yields"),
      type: "myHarvestQL",
      route: `/farmer/create-harvest/?cropId=${cropId}`,
    },
  ];

  const navigate = useNavigate();

  const handleClick = (route) => {
    navigate(route);
  };

  const handleBackToCropDetail = () => {
    navigate(`/farmer/crop/${cropId}`);
  };

  if (!farmId || !cropId) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <CustomChartsTitle>{t("Crop Trace Activities")}</CustomChartsTitle>
          <Typography color="error" sx={{ mt: 2 }}>
            {t("Farm or Crop ID is missing. Please check the URL.")}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={handleBackToCropDetail}
          >
            {`${t("Crop")}-${cropId}`}
          </Link>
          <Typography color="text.primary">{t("Trace Activities")}</Typography>
        </Breadcrumbs>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("Crop Trace Activities")}
          </Typography>

          <Grid container spacing={3}>
            {activities.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Fade in={true} timeout={500 + index * 100}>
                  <Card
                    elevation={2}
                    sx={{
                      height: "100%",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                        cursor: "pointer",
                      },
                    }}
                    onClick={() => handleClick(item.route)}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        p: 3,
                      }}
                    >
                      <Box sx={{ mb: 1.5 }}>
                        <Icon type={item.type} />
                      </Box>
                      <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </>
  );
};

export default CropTraceActivities;
